import React, { useRef, useState, useEffect } from 'react';
import { HeaderSettings, INITIAL_HEADER_SETTINGS } from './types';
import { CustomHeader } from './CustomHeader';
import { Settings, Image, Type, AlignLeft, AlignCenter, AlignRight, Layout, RotateCcw, Upload, FileType, Move, Save, Scaling, X, Download, Printer } from 'lucide-react';
import { db, storage, auth } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';

interface HeaderDesignerProps {
  settings: HeaderSettings | undefined;
  onUpdate: (newSettings: HeaderSettings) => void;
  onClose?: () => void;
}

type ActiveElement = 'logo' | 'schoolName' | 'address' | 'motto' | null;

export const HeaderDesigner: React.FC<HeaderDesignerProps> = ({ settings = INITIAL_HEADER_SETTINGS, onUpdate, onClose }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Interaction State
  const [activeElement, setActiveElement] = useState<ActiveElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialLogoState, setInitialLogoState] = useState({ x: 0, y: 0, width: 0 });
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Refs for auto-scrolling sidebar
  const sectionsRef = {
      logo: useRef<HTMLElement>(null),
      schoolName: useRef<HTMLDivElement>(null),
      address: useRef<HTMLDivElement>(null),
      motto: useRef<HTMLDivElement>(null),
      spacing: useRef<HTMLElement>(null)
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          const claims = idTokenResult.claims;
          console.log('User claims:', claims);
          
          // Check default affiliation first (as per Signup.tsx pattern)
          const defaultAff = claims.defaultAffiliation as { role: string; schoolID: string | null } | undefined;
          if (defaultAff && (defaultAff.role === 'school_staff' || defaultAff.role === 'admin' || defaultAff.role === 'owner') && defaultAff.schoolID) {
             setSchoolID(defaultAff.schoolID);
             console.log('Using schoolID from defaultAffiliation:', defaultAff.schoolID);
             loadHeaderSettings(defaultAff.schoolID);
             return;
          }

          // Fallback to affiliations array
          const affiliations = Array.isArray(claims.affiliations) ? claims.affiliations : [];
          const staffAffiliation = affiliations.find((aff: any) => 
            (aff.role === 'school_staff' || aff.role === 'admin' || aff.role === 'owner') && aff.schoolID
          );
          
          if (staffAffiliation?.schoolID) {
            setSchoolID(staffAffiliation.schoolID);
            console.log('Using schoolID from affiliations:', staffAffiliation.schoolID);
            loadHeaderSettings(staffAffiliation.schoolID);
          } else {
            console.warn("No valid school affiliation found for header editing.");
          }
        } catch (error) {
          console.error("Error fetching user claims:", error);
        }
      } else {
        console.log('No user authenticated');
      }
    });
    return () => unsubscribe();
  }, []);

  const loadHeaderSettings = async (id: string) => {
    console.log('Loading header settings for schoolID:', id);
    setIsLoading(true);
    try {
      // Always fetch school details first
      console.log('Fetching school details from:', `schools/${id}/schoolDetails/main`);
      const schoolDetailsDoc = await getDoc(doc(db, `schools/${id}/schoolDetails/main`));
      let schoolName = INITIAL_HEADER_SETTINGS.schoolName.text;
      let motto = INITIAL_HEADER_SETTINGS.motto.text;
      let address = INITIAL_HEADER_SETTINGS.address.text;
      let logoUrl = INITIAL_HEADER_SETTINGS.logo.url;
      // New fields
      let phone = '';
      let email = '';
      if (schoolDetailsDoc.exists()) {
        const data = schoolDetailsDoc.data();
        console.log('School details found:', data);
        if (data.name) schoolName = data.name;
        if (data.motto) motto = data.motto;
        if (data.address) address = data.address;
        if (data.logoUrl) logoUrl = data.logoUrl;
        if (data.phone) phone = data.phone;
        if (data.email) email = data.email;
      } else {
        console.log('No school details found');
      }

      // Combine address, phone, and email as in mock
      let combinedAddress = address;
      if (phone || email) {
        let contactLine = '';
        if (phone) contactLine += `Phone: ${phone}`;
        if (email) contactLine += (contactLine ? ' | ' : '') + `Email: ${email}`;
        combinedAddress = address ? `${address}\n${contactLine}` : contactLine;
      }

      // Then load header settings
      console.log('Fetching header settings from:', `schools/${id}/headerSettings/default`);
      const headerDoc = await getDoc(doc(db, `schools/${id}/headerSettings/default`));
      let headerData: Partial<HeaderSettings> = {};
      if (headerDoc.exists()) {
        headerData = headerDoc.data() as HeaderSettings;
        console.log('Header settings found:', headerData);
      } else {
        console.log('No header settings found, using defaults');
      }

      // Always override schoolName, motto, address, and logo with schoolDetails values if present
      onUpdate({
        ...INITIAL_HEADER_SETTINGS,
        ...headerData,
        schoolName: { ...INITIAL_HEADER_SETTINGS.schoolName, ...(headerData.schoolName || {}), text: schoolName },
        motto: { ...INITIAL_HEADER_SETTINGS.motto, ...(headerData.motto || {}), text: motto },
        address: { ...INITIAL_HEADER_SETTINGS.address, ...(headerData.address || {}), text: combinedAddress },
        logo: { ...INITIAL_HEADER_SETTINGS.logo, ...(headerData.logo || {}), url: logoUrl },
      });
    } catch (error) {
      console.error("Error loading header settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveHeader = async () => {
    if (!schoolID) {
      alert("School ID not found. Cannot save.");
      return;
    }
    setIsSaving(true);
    try {
      let logoUrl = settings.logo.url;

      // Upload logo if it's a base64 string
      if (logoUrl && logoUrl.startsWith('data:image')) {
        const storageRef = ref(storage, `schools/${schoolID}/header/logo.png`);
        await uploadString(storageRef, logoUrl, 'data_url');
        logoUrl = await getDownloadURL(storageRef);
      }

      const settingsToSave = {
        ...settings,
        logo: {
          ...settings.logo,
          url: logoUrl
        }
      };

      await setDoc(doc(db, `schools/${schoolID}/headerSettings/default`), settingsToSave);
      
      // Update local state with the new URL so we don't re-upload if saved again immediately
      onUpdate(settingsToSave);
      
      alert("Header settings saved successfully!");
    } catch (error) {
      console.error("Error saving header settings:", error);
      alert("Failed to save header settings.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // Auto-scroll to section when activeElement changes via preview click
    if (activeElement && sectionsRef[activeElement]?.current) {
        sectionsRef[activeElement].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activeElement]);

  const updateField = (section: keyof HeaderSettings, field: string, value: any) => {
    onUpdate({
      ...settings,
      [section]: {
        ...(settings[section] as any),
        [field]: value
      }
    });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdate({
          ...settings,
          logo: {
            ...settings.logo,
            url: reader.result as string
          }
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const resetDefaults = () => {
    if (window.confirm('Reset header settings to default?')) {
      onUpdate(INITIAL_HEADER_SETTINGS);
    }
  };

  const toggleEnabled = (enabled: boolean) => {
      onUpdate({ ...settings, isEnabled: enabled });
  };

  const handlePrint = () => {
    console.log("handlePrint called");
    const element = document.getElementById('header-preview-container');
    if (!element) {
        console.error("Preview container not found");
        alert("Preview container not found");
        return;
    }

    // Clone element to safeguard live preview
    const clone = element.cloneNode(true) as HTMLElement;
    
    // === CLEANUP: Remove all editor-specific elements and classes ===
    
    // Remove drag/resize handles and edit labels (these are editor-only UI)
    clone.querySelectorAll('[class*="cursor-nwse-resize"], [class*="cursor-move"]').forEach(el => {
      // Remove resize handles
      if (el.classList.contains('cursor-nwse-resize')) {
        el.remove();
      }
    });
    
    // Remove "Drag to move" labels and resize handles
    clone.querySelectorAll('.absolute.-top-6, .absolute.-bottom-2.-right-2').forEach(el => el.remove());
    
    // More specific: remove any element that looks like an edit control
    clone.querySelectorAll('[class*="bg-indigo-600"][class*="text-white"][class*="text-\\[10px\\]"]').forEach(el => el.remove());
    clone.querySelectorAll('[class*="bg-indigo-600"][class*="rounded-full"][class*="cursor-nwse-resize"]').forEach(el => el.remove());
    
    // Remove selection outlines and hover effects from all elements
    clone.querySelectorAll('*').forEach(el => {
      if (el instanceof HTMLElement) {
        // Remove classes that create visual artifacts in print
        const classesToRemove = [
          // Selection classes
          'outline', 'outline-2', 'outline-indigo-500', 'outline-dashed',
          'ring-2', 'ring-indigo-500',
          // Background highlights
          'bg-indigo-50/10', 'bg-indigo-50/5', 'bg-indigo-50/20',
          // Hover states
          'hover:outline', 'hover:outline-1', 'hover:outline-indigo-300',
          'hover:bg-indigo-50/5', 'hover:border-2', 'hover:border-indigo-300', 'hover:border-dashed',
          // Border selection styles
          'border-2', 'border-indigo-500', 'border-dashed', 'border-indigo-300',
          // Cursor styles (not needed in print)
          'cursor-pointer', 'cursor-default', 'cursor-move', 'cursor-nwse-resize',
          // Transition classes (can cause rendering issues)
          'transition-all', 'transition-colors', 'transition',
          // Group hover
          'group'
        ];
        
        classesToRemove.forEach(cls => {
          el.classList.remove(cls);
        });
        
        // Remove inline outline/ring styles if any
        el.style.outline = 'none';
        el.style.boxShadow = '';
      }
    });
    
    // Remove the "Custom Header Disabled" overlay if present
    clone.querySelectorAll('.pointer-events-none').forEach(el => {
      if (el.textContent?.includes('Custom Header Disabled')) {
        el.remove();
      }
    });
    
    // Remove grayscale/opacity if header is disabled (we want to print as-is)
    clone.querySelectorAll('.opacity-50, .grayscale').forEach(el => {
      if (el instanceof HTMLElement) {
        el.classList.remove('opacity-50', 'grayscale');
      }
    });
    
    // === Normalize dimensions for print (A4 width, no visual scaling) ===
    clone.style.transform = 'none';
    clone.style.boxShadow = 'none';
    clone.style.marginTop = '0';
    clone.style.marginBottom = '0';
    clone.style.marginLeft = '0';
    clone.style.marginRight = '0';
    
    // Explicitly set layout properties to match live preview exactly
    clone.style.width = '210mm';
    clone.style.minHeight = '148mm';
    clone.style.padding = '48px'; // p-12 = 3rem = 48px
    clone.style.boxSizing = 'border-box';
    clone.style.backgroundColor = 'white';
    clone.style.overflow = 'hidden';

    // Remove all container classes that might interfere with sizing
    clone.className = '';
    
    const printWindow = window.open('', '_blank', 'width=1000,height=800');
    if (!printWindow) {
        alert("Please allow popups to view the print preview.");
        return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Header Print Preview</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet">
          <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap" rel="stylesheet">
          <script src="https://cdn.tailwindcss.com"></script>
          <script>
             tailwind.config = {
                darkMode: 'class',
                theme: {
                  extend: {
                    fontFamily: {
                        sans: ['Inter', 'system-ui', 'sans-serif'],
                        'plus-jakarta': ['Plus Jakarta Sans', 'system-ui', 'sans-serif'],
                        'source-sans': ['Source Sans 3', 'system-ui', 'sans-serif'],
                    },
                    colors: {
                      slate: { 850: '#1e293b', 950: '#020617' }
                    }
                  }
                }
              }
          </script>
          <style>
            /* Reset all outlines, shadows, and selection indicators */
            * {
              outline: none !important;
              box-shadow: none !important;
            }
            
            /* Preserve only intentional shadows like the separator */
            [style*="backgroundColor"] {
              box-shadow: none !important;
            }
            
            @media print {
                @page { margin: 0; size: auto; }
                body { 
                    -webkit-print-color-adjust: exact; 
                    print-color-adjust: exact; 
                    margin: 0; 
                    padding: 0 !important;
                    display: block !important;
                }
                #print-wrapper { 
                  box-shadow: none !important; 
                  margin: 0; 
                }
                * {
                  outline: none !important;
                  box-shadow: none !important;
                }
            }
            body { 
                background-color: #f1f5f9; 
                display: flex; 
                justify-content: center; 
                padding: 40px; 
                min-height: 100vh;
                font-family: 'Inter', system-ui, sans-serif;
            }
            #print-wrapper {
                background: white;
                width: 210mm;
                box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
                box-sizing: border-box;
            }
            
            /* Hide any remaining edit controls that might have slipped through */
            [class*="cursor-nwse-resize"],
            [class*="Drag to move"] {
              display: none !important;
            }
          </style>
        </head>
        <body>
          <div id="print-wrapper">
             ${clone.outerHTML}
          </div>
          <script>
            // Clean up any remaining edit artifacts after DOM is ready
            document.addEventListener('DOMContentLoaded', () => {
              // Remove any lingering edit controls
              document.querySelectorAll('[class*="cursor-nwse-resize"]').forEach(el => el.remove());
              document.querySelectorAll('[class*="bg-indigo-600"][class*="rounded-full"]').forEach(el => {
                if (el.querySelector('svg') || el.textContent?.trim() === '') {
                  el.remove();
                }
              });
              // Remove "Drag to move" labels
              document.querySelectorAll('div').forEach(el => {
                if (el.textContent?.includes('Drag to move')) {
                  el.remove();
                }
              });
            });
            
            // Auto-print after styles load
            window.onload = () => {
                setTimeout(() => {
                    window.print();
                }, 800);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

    const handleDownloadPDF = async () => {
    console.log("handleDownloadPDF called");
    const element = document.getElementById('header-preview-container');
    if (!element) {
        alert("Preview container not found.");
        return;
    }

    // @ts-ignore
    if (typeof window.html2pdf === 'undefined') {
        alert("PDF generator is initializing. Please wait a moment.");
        return;
    }

    setIsDownloading(true);

    try {
        console.log("Starting PDF generation via iframe method");

        // 1. Prepare the content EXACTLY like handlePrint does
        const clone = element.cloneNode(true) as HTMLElement;
        
        // === CLEANUP: Remove all editor-specific elements and classes ===
        
        // Remove drag/resize handles and edit labels (these are editor-only UI)
        clone.querySelectorAll('[class*="cursor-nwse-resize"], [class*="cursor-move"]').forEach(el => {
          if (el.classList.contains('cursor-nwse-resize')) {
            el.remove();
          }
        });
        
        // Remove "Drag to move" labels and resize handles
        clone.querySelectorAll('.absolute.-top-6, .absolute.-bottom-2.-right-2').forEach(el => el.remove());
        
        // More specific: remove any element that looks like an edit control
        clone.querySelectorAll('[class*="bg-indigo-600"][class*="text-white"][class*="text-\\[10px\\]"]').forEach(el => el.remove());
        clone.querySelectorAll('[class*="bg-indigo-600"][class*="rounded-full"][class*="cursor-nwse-resize"]').forEach(el => el.remove());
        
        // Remove selection outlines and hover effects from all elements
        clone.querySelectorAll('*').forEach(el => {
          if (el instanceof HTMLElement) {
            const classesToRemove = [
              'outline', 'outline-2', 'outline-indigo-500', 'outline-dashed',
              'ring-2', 'ring-indigo-500',
              'bg-indigo-50/10', 'bg-indigo-50/5', 'bg-indigo-50/20',
              'hover:outline', 'hover:outline-1', 'hover:outline-indigo-300',
              'hover:bg-indigo-50/5', 'hover:border-2', 'hover:border-indigo-300', 'hover:border-dashed',
              'border-2', 'border-indigo-500', 'border-dashed', 'border-indigo-300',
              'cursor-pointer', 'cursor-default', 'cursor-move', 'cursor-nwse-resize',
              'transition-all', 'transition-colors', 'transition',
              'group'
            ];
            classesToRemove.forEach(cls => el.classList.remove(cls));
            el.style.outline = 'none';
            el.style.boxShadow = '';
          }
        });
        
        // Remove the "Custom Header Disabled" overlay if present
        clone.querySelectorAll('.pointer-events-none').forEach(el => {
          if (el.textContent?.includes('Custom Header Disabled')) {
            el.remove();
          }
        });
        
        // Remove grayscale/opacity if header is disabled
        clone.querySelectorAll('.opacity-50, .grayscale').forEach(el => {
          if (el instanceof HTMLElement) {
            el.classList.remove('opacity-50', 'grayscale');
          }
        });
        
        // === Normalize dimensions (same as handlePrint) ===
        clone.style.transform = 'none';
        clone.style.boxShadow = 'none';
        clone.style.marginTop = '0';
        clone.style.marginBottom = '0';
        clone.style.marginLeft = '0';
        clone.style.marginRight = '0';
        
        // Explicitly set layout properties to match live preview exactly
        clone.style.width = '210mm';
        clone.style.minHeight = '148mm';
        clone.style.padding = '48px'; // p-12 = 3rem = 48px
        clone.style.boxSizing = 'border-box';
        clone.style.backgroundColor = 'white';
        clone.style.overflow = 'hidden'; // Prevent any spillover

        // Remove all classes that might interfere (scales, margins, shadows, etc.)
        // We only keep the content styling classes inside
        clone.className = ''; 
        // Re-add a clean class if needed, or just rely on inline styles. 
        // Since we set everything inline above, we can strip the container classes.

        // 2. Create a hidden iframe to render the content in a clean environment
        // This avoids the local Tailwind v4 oklch issues by using the CDN version (v3)
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '210mm';
        iframe.style.height = '297mm';
        iframe.style.visibility = 'hidden';
        iframe.style.left = '-9999px';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow?.document;
        if (!iframeDoc) {
            throw new Error("Could not create iframe document");
        }

        // 3. Write the HTML structure (Based on handlePrint, but optimized for PDF capture)
        const html = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>PDF Generator</title>
              <link rel="preconnect" href="https://fonts.googleapis.com">
              <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
              <link href="https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap" rel="stylesheet">
              <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&display=swap" rel="stylesheet">
              <script src="https://cdn.tailwindcss.com"></script>
              <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
              <script>
                 tailwind.config = {
                    darkMode: 'class',
                    theme: {
                      extend: {
                        fontFamily: {
                            sans: ['Inter', 'system-ui', 'sans-serif'],
                            'plus-jakarta': ['Plus Jakarta Sans', 'system-ui', 'sans-serif'],
                            'source-sans': ['Source Sans 3', 'system-ui', 'sans-serif'],
                        },
                        colors: {
                          slate: { 850: '#1e293b', 950: '#020617' }
                        }
                      }
                    }
                  }
              </script>
              <style>
                /* Reset all outlines, shadows, and selection indicators */
                * {
                  outline: none !important;
                  box-shadow: none !important;
                }
                
                body { 
                    background-color: white; 
                    margin: 0; 
                    padding: 0;
                    font-family: 'Inter', system-ui, sans-serif;
                }
                #print-wrapper {
                    width: 210mm;
                    min-height: 297mm;
                    background: white;
                    padding: 0;
                    margin: 0;
                    box-sizing: border-box;
                }
                
                /* Hide any remaining edit controls */
                [class*="cursor-nwse-resize"],
                [class*="Drag to move"] {
                  display: none !important;
                }
              </style>
            </head>
            <body>
              <div id="print-wrapper">
                 ${clone.outerHTML}
              </div>
            </body>
          </html>
        `;
        
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();

        // 4. Wait for resources to load
        await new Promise(resolve => {
            iframe.onload = resolve;
        });
        
        // 5. Give Tailwind CDN time to process classes and generate styles
        await new Promise(r => setTimeout(r, 1500));

        // 6. Trigger html2pdf INSIDE the iframe
        const iframeWindow = iframe.contentWindow as any;
        
        if (iframeWindow && iframeWindow.html2pdf) {
            const target = iframeDoc.getElementById('print-wrapper');
            const opt = {
                margin: 0,
                filename: 'Header_Design.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true, logging: false },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            
            await iframeWindow.html2pdf().set(opt).from(target).save();
            console.log("PDF generation finished");
        } else {
            throw new Error("html2pdf library not loaded in iframe");
        }

        // 7. Cleanup
        // Small delay to allow save to trigger before removing iframe
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 1000);

    } catch (err) {
        console.error("PDF generation failed:", err);
        alert("Failed to generate PDF. Please try again.");
    } finally {
        setIsDownloading(false);
    }
  };

  // --- Interaction Handlers ---

  const getScale = () => {
    if (!paperRef.current) return 1;
    const rect = paperRef.current.getBoundingClientRect();
    return rect.width / 793.7; 
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!settings.logo.customPositioning) return;
    e.preventDefault(); 
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialLogoState({ 
        x: settings.logo.x, 
        y: settings.logo.y, 
        width: settings.logo.width 
    });
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    if (!settings.logo.customPositioning) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialLogoState({ 
        x: settings.logo.x, 
        y: settings.logo.y, 
        width: settings.logo.width 
    });
  };

  useEffect(() => {
    const handleWindowMouseMove = (e: MouseEvent) => {
        if (!isDragging && !isResizing) return;
        
        const scale = getScale();
        const deltaX = (e.clientX - dragStart.x) / scale;
        const deltaY = (e.clientY - dragStart.y) / scale;

        if (isDragging) {
            onUpdate({
                ...settings,
                logo: {
                    ...settings.logo,
                    x: initialLogoState.x + deltaX,
                    y: initialLogoState.y + deltaY
                }
            });
        } else if (isResizing) {
            const newWidth = Math.max(20, initialLogoState.width + deltaX);
            updateField('logo', 'width', newWidth);
        }
    };

    const handleWindowMouseUp = () => {
        setIsDragging(false);
        setIsResizing(false);
    };

    if (isDragging || isResizing) {
        window.addEventListener('mousemove', handleWindowMouseMove);
        window.addEventListener('mouseup', handleWindowMouseUp);
    }

    return () => {
        window.removeEventListener('mousemove', handleWindowMouseMove);
        window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [isDragging, isResizing, dragStart, initialLogoState, settings]);

  // Helper style for active section
  const getSectionStyle = (section: ActiveElement) => 
      activeElement === section 
      ? 'ring-2 ring-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/20 rounded-xl p-3 -m-3 transition-all duration-300 relative z-10' 
      : 'transition-all duration-300 relative z-0';


  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-0 bg-slate-100 dark:bg-slate-900">
      
      {/* LEFT CONTROL PANEL */}
      <div ref={sidebarRef} className="lg:col-span-4 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 overflow-y-auto h-full shadow-lg z-10 scroll-smooth scrollbar-sidebar transition-colors">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 sticky top-0 z-20 flex justify-between items-center">
            <div>
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                    <Layout size={18} className="text-indigo-600 dark:text-indigo-400"/> Header Designer
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Customize your document header</p>
            </div>
            <div className="flex items-center gap-2">
                {onClose && (
                  <button 
                    onClick={onClose}
                    className="p-1.5 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    title="Close Designer"
                  >
                    <X size={20} />
                  </button>
                )}
                <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={settings.isEnabled}
                        onChange={(e) => toggleEnabled(e.target.checked)}
                    />
                    <div className="w-9 h-5 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
            </div>
        </div>

        <div className="p-6 space-y-10">
            {!schoolID && !isLoading && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 text-red-800 dark:text-red-200 px-4 py-3 rounded-md text-sm mb-4">
                    <strong>Error:</strong> Could not identify School ID. Please ensure you are logged in with a valid school account.
                </div>
            )}

            {!settings.isEnabled && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/50 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded-md text-sm mb-4">
                    <strong>Note:</strong> Custom header is currently disabled. Toggle the switch above to apply these settings to your report card.
                </div>
            )}

            {/* Layout Section */}
            <section className="space-y-3">
                <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2 mb-3">
                    <Layout size={14} /> Layout Style
                </h3>
                <div className="grid grid-cols-3 gap-2">
                    {(['classic', 'centered', 'two-column'] as const).map((l) => (
                        <button
                            key={l}
                            onClick={() => onUpdate({...settings, layout: l})}
                            disabled={settings.logo.customPositioning}
                            className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-all 
                            ${settings.logo.customPositioning ? 'opacity-40 cursor-not-allowed border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800' : 
                                settings.layout === l ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-1 ring-indigo-500' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'}`}
                        >
                            <div className="w-full h-8 bg-white dark:bg-slate-600 border border-slate-300 dark:border-slate-500 rounded overflow-hidden p-1 relative">
                                {l === 'classic' && <div className="flex gap-1 h-full items-center"><div className="w-2 h-2 bg-slate-400 dark:bg-slate-300 rounded-full"></div> <div className="h-1 w-full bg-slate-200 dark:bg-slate-400"></div></div>}
                                {l === 'centered' && <div className="flex flex-col gap-1 h-full items-center justify-center"><div className="w-2 h-2 bg-slate-400 dark:bg-slate-300 rounded-full"></div> <div className="h-1 w-8 bg-slate-200 dark:bg-slate-400"></div></div>}
                                {l === 'two-column' && <div className="flex justify-between gap-1 h-full items-center"><div className="flex gap-1 items-center"><div className="w-2 h-2 bg-slate-400 dark:bg-slate-300 rounded-full"></div></div> <div className="h-1 w-4 bg-slate-200 dark:bg-slate-400"></div></div>}
                            </div>
                            <span className="text-xs font-medium capitalize">{l}</span>
                        </button>
                    ))}
                </div>
                {settings.logo.customPositioning && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 italic">Layout options disabled in freeform mode.</p>
                )}
            </section>

            <hr className="border-slate-100 dark:border-slate-700" />

            {/* Header Dimensions */}
            <section className="space-y-4">
                 <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2 mb-3">
                    <Scaling size={14} /> Header Dimensions
                </h3>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
                             <span>Overall Scale</span>
                             <span>{Math.round((settings.scale || 1) * 100)}%</span>
                        </div>
                        <input 
                            type="range" 
                            min="0.5" 
                            max="1.5" 
                            step="0.05"
                            value={settings.scale || 1} 
                            onChange={(e) => onUpdate({...settings, scale: parseFloat(e.target.value)})}
                            className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <div>
                        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400 mb-2">
                             <span>Vertical Position (Top Margin)</span>
                             <span>{settings.spacing.marginTop}px</span>
                        </div>
                        <input 
                            type="range" 
                            min="-100" 
                            max="200" 
                            value={settings.spacing.marginTop} 
                            onChange={(e) => updateField('spacing', 'marginTop', parseInt(e.target.value))}
                            className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer mb-2"
                        />
                        <input 
                            type="number"
                            value={settings.spacing.marginTop}
                            onChange={(e) => updateField('spacing', 'marginTop', parseInt(e.target.value))}
                            className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                        />
                    </div>
                </div>
            </section>

            <hr className="border-slate-100 dark:border-slate-700" />

            {/* Logo Controls */}
            <section ref={sectionsRef.logo} className={`space-y-4 ${getSectionStyle('logo')}`}>
                 <div className="flex justify-between items-center mb-4 py-2">
                    <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${activeElement === 'logo' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        <Image size={14} /> Logo Settings
                    </h3>
                    
                    {/* Freeform Toggle */}
                    <div className="flex items-center gap-4">
                         <span className={`text-[10px] uppercase font-bold ${settings.logo.customPositioning ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`}>Freeform Mode</span>
                         <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={settings.logo.customPositioning || false}
                                onChange={(e) => updateField('logo', 'customPositioning', e.target.checked)}
                            />
                            <div className="w-7 h-4 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-indigo-600"></div>
                        </label>
                    </div>
                 </div>
                
                <div className="flex items-start gap-4">
                    <div 
                        className="w-16 h-16 bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:opacity-80 transition relative group"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {settings.logo.url ? (
                            <img src={settings.logo.url} className="w-full h-full object-contain" alt="Logo" />
                        ) : (
                            <Upload size={20} className="text-slate-400 dark:text-slate-500" />
                        )}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                            <Upload size={16} className="text-white" />
                        </div>
                    </div>
                    <div className="flex-1">
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleLogoUpload} 
                            className="hidden" 
                            accept="image/*"
                        />
                        <button 
                            onClick={() => fileInputRef.current?.click()}
                            className="text-sm bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 px-3 py-1.5 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 w-full mb-4 shadow-sm"
                        >
                            Upload New Logo
                        </button>
                        <div className="flex justify-between text-xs text-slate-400 mb-2">
                             <span>Size: {settings.logo.width}px</span>
                        </div>
                        <input 
                            type="range" 
                            min="20" 
                            max="300" 
                            value={settings.logo.width} 
                            onChange={(e) => updateField('logo', 'width', parseInt(e.target.value))}
                            className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer mt-2"
                        />
                    </div>
                </div>

                {!settings.logo.customPositioning ? (
                    <div className="grid grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Alignment</label>
                            <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-md">
                                {(['left', 'center', 'right'] as const).map(align => (
                                    <button
                                        key={align}
                                        onClick={() => updateField('logo', 'alignment', align)}
                                        className={`flex-1 p-1 rounded flex justify-center ${settings.logo.alignment === align ? 'bg-white dark:bg-slate-600 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                                    >
                                        {align === 'left' && <AlignLeft size={14} />}
                                        {align === 'center' && <AlignCenter size={14} />}
                                        {align === 'right' && <AlignRight size={14} />}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Padding</label>
                            <input 
                                type="number" 
                                value={settings.logo.padding}
                                onChange={(e) => updateField('logo', 'padding', parseInt(e.target.value))}
                                className="w-full p-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-2"
                            />
                        </div>
                    </div>
                ) : (
                     <div className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 rounded-md p-3 text-xs text-indigo-800 dark:text-indigo-200 flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                         <Move size={14} className="mt-0.5 shrink-0" />
                         <div>
                             <p className="font-bold mb-1">Freeform Active</p>
                             <p>Drag the logo in the preview panel to position it. Use the handle on the bottom-right to resize.</p>
                             <div className="grid grid-cols-2 gap-2 mt-2">
                                 <div>
                                     <span className="text-indigo-400 uppercase text-[10px] font-bold">X Pos</span>
                                     <input 
                                        type="number" 
                                        value={Math.round(settings.logo.x)} 
                                        onChange={(e) => updateField('logo', 'x', parseInt(e.target.value))}
                                        className="w-full text-[10px] p-1 border rounded bg-white dark:bg-slate-900 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                                    />
                                 </div>
                                 <div>
                                     <span className="text-indigo-400 uppercase text-[10px] font-bold">Y Pos</span>
                                     <input 
                                        type="number" 
                                        value={Math.round(settings.logo.y)} 
                                        onChange={(e) => updateField('logo', 'y', parseInt(e.target.value))}
                                        className="w-full text-[10px] p-1 border rounded bg-white dark:bg-slate-900 dark:border-slate-700 text-slate-900 dark:text-slate-100"
                                    />
                                 </div>
                             </div>
                         </div>
                     </div>
                )}
            </section>

             <hr className="border-slate-100 dark:border-slate-700" />

             {/* School Name Controls */}
             <div ref={sectionsRef.schoolName} className={`space-y-4 ${getSectionStyle('schoolName')}`}>
                 <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${activeElement === 'schoolName' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`}>
                    <Type size={14} /> School Name
                </h3>
                <input 
                    type="text" 
                    value={settings.schoolName.text}
                    onFocus={() => setActiveElement('schoolName')}
                    onChange={(e) => updateField('schoolName', 'text', e.target.value)}
                    className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-4"
                    placeholder="School Name"
                />
                
                <div className="grid grid-cols-2 gap-3">
                     <div>
                        <label htmlFor="font-family-select" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Font Family</label>
                        <select
                            id="font-family-select"
                            value={settings.schoolName.fontFamily}
                            onFocus={() => setActiveElement('schoolName')}
                            onChange={(e) => updateField('schoolName', 'fontFamily', e.target.value)}
                            className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-2"
                        >
                            <option value="sans">Sans Serif</option>
                            <option value="serif">Serif</option>
                            <option value="mono">Monospace</option>
                        </select>
                     </div>
                     <div>
                         <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Color</label>
                         <div className="flex items-center gap-2 mb-2">
                             <input 
                                type="color" 
                                value={settings.schoolName.color}
                                onFocus={() => setActiveElement('schoolName')}
                                onChange={(e) => updateField('schoolName', 'color', e.target.value)}
                                className="w-8 h-8 rounded border border-slate-300 dark:border-slate-600 p-0.5 bg-white cursor-pointer"
                             />
                             <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{settings.schoolName.color}</span>
                         </div>
                     </div>
                </div>

                <div className="flex gap-4">
                     <div className="flex-1">
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Font Size: {settings.schoolName.fontSize}px</label>
                        <input 
                            type="range" 
                            min="12" 
                            max="60" 
                            value={settings.schoolName.fontSize} 
                            onFocus={() => setActiveElement('schoolName')}
                            onChange={(e) => updateField('schoolName', 'fontSize', parseInt(e.target.value))}
                            className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer mt-2"
                        />
                     </div>
                     <div className="flex items-end">
                         <button 
                            onClick={() => updateField('schoolName', 'bold', !settings.schoolName.bold)}
                            className={`px-3 py-1.5 rounded border text-sm font-bold ${settings.schoolName.bold ? 'bg-indigo-50 dark:bg-indigo-900/40 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-400' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400'}`}
                         >
                            B
                         </button>
                     </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Text Alignment</label>
                    <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-md w-full mt-2">
                        {(['left', 'center', 'right'] as const).map(align => (
                            <button
                                key={align}
                                onClick={() => updateField('schoolName', 'alignment', align)}
                                className={`flex-1 p-1 rounded flex justify-center ${settings.schoolName.alignment === align ? 'bg-white dark:bg-slate-600 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                            >
                                {align === 'left' && <AlignLeft size={14} />}
                                {align === 'center' && <AlignCenter size={14} />}
                                {align === 'right' && <AlignRight size={14} />}
                            </button>
                        ))}
                    </div>
                </div>
             </div>

             <hr className="border-slate-100 dark:border-slate-700" />

             {/* Address & Motto Controls */}
             <section className="space-y-6">
                 
                 {/* Motto Subsection */}
                 <div ref={sectionsRef.motto} className={`space-y-4 ${getSectionStyle('motto')}`}>
                    <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${activeElement === 'motto' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        <FileType size={14} /> Motto
                    </h3>
                    <div>
                        <input
                            type="text"
                            value={settings.motto.text}
                            onFocus={() => setActiveElement('motto')}
                            onChange={(e) => updateField('motto', 'text', e.target.value)}
                            className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-4"
                            placeholder="School Motto"
                        />
                    </div>
                    <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={settings.motto.italic}
                                onChange={(e) => updateField('motto', 'italic', e.target.checked)}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            Italic
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={settings.motto.showDivider}
                                onChange={(e) => updateField('motto', 'showDivider', e.target.checked)}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            Show Divider
                        </label>
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Text Alignment</label>
                        <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-md w-full">
                            {(['left', 'center', 'right'] as const).map(align => (
                                <button
                                    key={align}
                                    onClick={() => updateField('motto', 'alignment', align)}
                                    className={`flex-1 p-1 rounded flex justify-center ${settings.motto.alignment === align ? 'bg-white dark:bg-slate-600 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                                >
                                    {align === 'left' && <AlignLeft size={14} />}
                                    {align === 'center' && <AlignCenter size={14} />}
                                    {align === 'right' && <AlignRight size={14} />}
                                </button>
                            ))}
                        </div>
                    </div>
                 </div>

                 {/* Address Subsection */}
                 <div ref={sectionsRef.address} className={`space-y-4 ${getSectionStyle('address')}`}>
                     <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${activeElement === 'address' ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        <FileType size={14} /> Address & Contact
                    </h3>
                    <div>
                        <textarea
                            value={settings.address.text}
                            onFocus={() => setActiveElement('address')}
                            onChange={(e) => updateField('address', 'text', e.target.value)}
                            className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none h-20 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-4"
                            placeholder="Enter address..."
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Font Size</label>
                            <input 
                                type="number"
                                value={settings.address.fontSize}
                                onFocus={() => setActiveElement('address')}
                                onChange={(e) => updateField('address', 'fontSize', parseInt(e.target.value))}
                                className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-2"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Line Height</label>
                            <input 
                                type="number"
                                step="0.1"
                                value={settings.address.lineHeight}
                                onFocus={() => setActiveElement('address')}
                                onChange={(e) => updateField('address', 'lineHeight', parseFloat(e.target.value))}
                                className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-2"
                            />
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Text Alignment</label>
                        <div className="flex bg-slate-100 dark:bg-slate-700 p-1 rounded-md w-full">
                            {(['left', 'center', 'right'] as const).map(align => (
                                <button
                                    key={align}
                                    onClick={() => updateField('address', 'alignment', align)}
                                    className={`flex-1 p-1 rounded flex justify-center ${settings.address.alignment === align ? 'bg-white dark:bg-slate-600 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
                                >
                                    {align === 'left' && <AlignLeft size={14} />}
                                    {align === 'center' && <AlignCenter size={14} />}
                                    {align === 'right' && <AlignRight size={14} />}
                                </button>
                            ))}
                        </div>
                    </div>
                 </div>
             </section>

             <hr className="border-slate-100 dark:border-slate-700" />
             
             {/* Spacing */}
             <section ref={sectionsRef.spacing} className="space-y-4">
                 <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Settings size={14} /> Page Spacing
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Top Margin (px)</label>
                        <input 
                            type="number"
                            value={settings.spacing.marginTop}
                            onChange={(e) => updateField('spacing', 'marginTop', parseInt(e.target.value))}
                            className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-2"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Bottom Margin (px)</label>
                        <input 
                            type="number"
                            value={settings.spacing.marginBottom}
                            onChange={(e) => updateField('spacing', 'marginBottom', parseInt(e.target.value))}
                            className="w-full p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 mb-2"
                        />
                    </div>
                </div>
                <div className="w-full flex justify-center mt-2">
                    <input 
                        type="range" 
                        min="-100" 
                        max="200" 
                        value={settings.spacing.marginTop} 
                        onChange={(e) => updateField('spacing', 'marginTop', parseInt(e.target.value))}
                        className="w-3/4 accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
             </section>

             <hr className="border-slate-100 dark:border-slate-700" />

             {/* Separator Line */}
             <section className="space-y-4">
                 <h3 className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-2">
                    <Settings size={14} /> Separator Line
                </h3>
                
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Show Separator Line</label>
                    <button
                        onClick={() => updateField('separator', 'enabled', !settings.separator?.enabled)}
                        title={settings.separator?.enabled ? 'Disable separator line' : 'Enable separator line'}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                            settings.separator?.enabled ? 'bg-indigo-600' : 'bg-slate-300 dark:bg-slate-600'
                        }`}
                    >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${
                            settings.separator?.enabled ? 'translate-x-5' : ''
                        }`} />
                    </button>
                </div>

                {settings.separator?.enabled && (
                    <>
                        {/* Line Color */}
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Line Color</label>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="color"
                                    value={settings.separator?.color || '#e2e8f0'}
                                    onChange={(e) => updateField('separator', 'color', e.target.value)}
                                    className="w-10 h-10 rounded-md border border-slate-300 dark:border-slate-600 cursor-pointer"
                                />
                                <input 
                                    type="text"
                                    value={settings.separator?.color || '#e2e8f0'}
                                    onChange={(e) => updateField('separator', 'color', e.target.value)}
                                    className="flex-1 p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
                                />
                            </div>
                        </div>

                        {/* Line Thickness */}
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                Line Thickness: {settings.separator?.thickness ?? 1}px
                            </label>
                            <input 
                                type="range" 
                                min="1" 
                                max="10" 
                                value={settings.separator?.thickness ?? 1}
                                onChange={(e) => updateField('separator', 'thickness', parseInt(e.target.value))}
                                className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        {/* Gap from Header */}
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                Gap from Header: {settings.separator?.marginTop ?? 10}px
                            </label>
                            <input 
                                type="range" 
                                min="0" 
                                max="50" 
                                value={settings.separator?.marginTop ?? 10}
                                onChange={(e) => updateField('separator', 'marginTop', parseInt(e.target.value))}
                                className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        {/* Horizontal Padding (Edge Distance) */}
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                Distance from Edges: {settings.separator?.horizontalPadding ?? 0}px
                            </label>
                            <input 
                                type="range" 
                                min="0" 
                                max="100" 
                                value={settings.separator?.horizontalPadding ?? 0}
                                onChange={(e) => updateField('separator', 'horizontalPadding', parseInt(e.target.value))}
                                className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>

                        {/* Gap below line (to content) */}
                        <div>
                            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                                Gap to Content Below: {settings.separator?.marginBottom ?? 10}px
                            </label>
                            <input 
                                type="range" 
                                min="0" 
                                max="50" 
                                value={settings.separator?.marginBottom ?? 10}
                                onChange={(e) => updateField('separator', 'marginBottom', parseInt(e.target.value))}
                                className="w-full accent-indigo-600 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                    </>
                )}
             </section>

             <div className="pt-6 pb-20 space-y-3">
                 <button 
                    onClick={saveHeader}
                    disabled={isSaving || isLoading || !schoolID}
                    className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-sm font-bold flex items-center justify-center gap-2 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed m-2"
                 >
                     <Save size={14} /> {isSaving ? 'Saving...' : 'Save School Header'}
                 </button>
                 <div className="grid grid-cols-2 gap-3">
                    <button 
                        onClick={resetDefaults}
                        className="py-2 border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium flex items-center justify-center gap-2 transition m-2"
                    >
                        <RotateCcw size={14} /> Reset
                    </button>
                    <button 
                        onClick={handlePrint}
                        className="py-2 border border-slate-300 dark:border-slate-600 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium flex items-center justify-center gap-2 transition m-2"
                    >
                        <Printer size={14} /> Print
                    </button>
                 </div>
                 <button 
                    onClick={handleDownloadPDF}
                    disabled={isDownloading}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-bold flex items-center justify-center gap-2 transition shadow-sm disabled:opacity-70 disabled:cursor-not-allowed m-2"
                 >
                     {isDownloading ? (
                        <>Generating...</>
                     ) : (
                        <><Download size={14} /> Download PDF</>
                     )}
                 </button>
             </div>
        </div>
      </div>

      {/* RIGHT PREVIEW PANEL */}
      <div className="lg:col-span-8 bg-slate-200 dark:bg-slate-950 h-full overflow-y-auto flex items-start justify-center p-8 relative transition-colors scrollbar-preview" onClick={() => setActiveElement(null)}>
        <div 
            id="header-preview-container"
            ref={paperRef}
            className="bg-white shadow-2xl w-[210mm] min-h-[148mm] mx-auto transition-transform duration-300 origin-top scale-[0.6] sm:scale-75 lg:scale-90 xl:scale-100 mt-[-20px] lg:mt-0 p-12 cursor-default"
            onClick={() => setActiveElement(null)}
        >
            {/* Render the Custom Header */}
            <div className={`relative ${!settings.isEnabled ? 'opacity-50 grayscale' : ''}`}>
                <CustomHeader 
                    settings={settings} 
                    isEditable={true} 
                    selectedElement={activeElement}
                    onElementSelect={setActiveElement}
                    onLogoMouseDown={handleMouseDown}
                    onResizeMouseDown={handleResizeStart}
                />
                
                {/* Dummy content removed per request */}

                 {!settings.isEnabled && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black/80 text-white px-6 py-3 rounded-full text-sm font-bold shadow-xl backdrop-blur-sm">
                            Custom Header Disabled
                        </div>
                    </div>
                )}
            </div>
        </div>
        
        <div className="absolute top-4 right-4 bg-white/90 dark:bg-slate-800/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold text-slate-500 dark:text-slate-300 shadow-sm border border-slate-200 dark:border-slate-700 pointer-events-none">
            Live Preview (A4 Width)
        </div>
      </div>
    </div>
  );
};